<template>
  <!-- ★ v29.29：本地静态选项列表（select/radio/checkbox/groupselect）——原版
       FormDesignModal/FieldSetting.vue「数据源」页的「选项列表」移植（原版仅含这三类）。
       落库形状对齐 lybase ADVANCED_SEARCH + choices 契约：`config.options = [{value, label}]`
       （`util/field_config.py` 由 Django choices/BooleanField 产出的就是这一形状，且不写 valueKey/labelKey；
       运行时 `SmallTableSerarchField.vue` 按 `option[valueKey||'value'] / option[labelKey||'label']` 取值）。 -->
  <a-col :span="24">
    <div class="section-title">
      <span>选项列表</span>
      <a-button
        type="primary"
        size="small"
        @click="addRow"
      >
        + 添加选项
      </a-button>
      <a-button
        v-if="importable"
        size="small"
        @click="importFromContentMap"
      >
        从「转换/颜色」导入
      </a-button>
      <span class="opt-tip">值类型按该列在 SQL 中的实际类型选择（数值列勿用字符，Oracle 下会直接比较失败）。</span>
    </div>
    <div
      v-if="!rows.length"
      class="opt-empty"
    >
      暂无选项 —— 下拉选框/单选框/多选框至少需要一个选项才能使用。
    </div>
    <div
      v-for="(row, i) in rows"
      :key="i"
      class="row-line"
    >
      <a-select
        v-model:value="row.valueType"
        class="w-type"
        :options="VALUE_TYPE_OPTIONS"
        @change="onValueTypeChange(row)"
      />
      <a-select
        v-if="row.valueType === 'bool'"
        v-model:value="row.value"
        class="w-val"
        placeholder="值"
        :options="BOOL_OPTIONS"
      />
      <a-input-number
        v-else-if="row.valueType === 'int'"
        v-model:value="row.value"
        class="w-val"
        placeholder="值"
      />
      <a-input
        v-else
        v-model:value="row.value"
        class="w-val"
        placeholder="值"
        allow-clear
      />
      <a-input
        v-model:value="row.label"
        class="w-label"
        placeholder="显示文本"
        allow-clear
      />
      <a-button
        type="link"
        danger
        @click="rows.splice(i, 1)"
      >
        删除
      </a-button>
    </div>
  </a-col>
</template>

<script setup lang="ts">
/* 选项列表编辑器：本地维护 rows（含 valueType，便于按类型选输入控件），
   任何编辑立即回写 `config.options = [{value, label}]`（实时镜像 JSON 页的同源语义）。
   与 TreeDataConfig 同款「config 引用变化 → 重新初始化」做法：外壳切类型/JSON 页回写会整体替换 config。 */
import { nextTick, ref, watch } from 'vue';
import { BOOL_OPTIONS } from './constants';

const props = defineProps<{
  config: any;
  /** 列配置行：用 class_name 定新选项的值类型默认值、用 content_map 做「导入」 */
  record?: any;
}>();

const VALUE_TYPE_OPTIONS = [
  { value: 'int', label: '数字' },
  { value: 'str', label: '字符' },
  { value: 'bool', label: '布尔' },
];

const rows = ref<any[]>([]);
/* 回写自身触发的同步重入守卫：写回 config.options 会命中下面的 watch，靠「内容相等就跳过」收敛 */
let syncing = false;

const inferValueType = (v: any) =>
  typeof v === 'boolean' ? 'bool' : typeof v === 'number' ? 'int' : 'str';

/** SQL 列类型 → 默认值类型（class_name 由列探测写入） */
const defaultValueType = () => {
  const cls = String(props.record?.class_name || '').toLowerCase();
  if (cls === 'int' || cls === 'float' || cls === 'decimal' || cls === 'number') return 'int';
  if (cls === 'bool' || cls === 'boolean') return 'bool';
  return 'str';
};

const syncFromConfig = () => {
  const opts = Array.isArray(props.config?.options) ? props.config.options : [];
  const next = opts.map((o: any) => ({
    valueType: inferValueType(o?.value),
    value: o?.value ?? null,
    // 兼容历史上按创建/编辑表单形状写的 {value, name}（发布前的老配置）
    label: o?.label ?? o?.name ?? '',
  }));
  if (JSON.stringify(next) === JSON.stringify(rows.value)) return; // 无变化 → 不动，避免回写循环
  syncing = true;
  rows.value = next;
  nextTick(() => {
    syncing = false;
  });
};
watch(() => props.config?.options, syncFromConfig, { immediate: true });

/* 值类型切换：把已填的值按新类型归一（否则 a-select 拿 true 去比对字符串 'true' 会不匹配） */
const coerceValue = (row: any) => {
  const v = row.value;
  if (row.valueType === 'bool') {
    if (v === true || v === 'true' || v === 1 || v === '1') return true;
    if (v === false || v === 'false' || v === 0 || v === '0') return false;
    return null;
  }
  if (row.valueType === 'int') {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  return v === null || v === undefined ? '' : String(v);
};
const onValueTypeChange = (row: any) => {
  row.value = coerceValue(row);
};

const writeBack = () => {
  if (syncing) return;
  props.config.options = rows.value.map((r: any) => ({
    value: coerceValue(r),
    label: r.label == null ? '' : String(r.label),
  }));
};
watch(rows, writeBack, { deep: true });

const addRow = () => {
  rows.value.push({ valueType: defaultValueType(), value: null, label: '' });
};

/* ───────── 从「转换/颜色」(content_map) 导入 ─────────
   ★ 必要性：后端 `_adv_search_item` 只在【没有人工搜索组件】时才用 content_map 自动推断
   `type='select' + options`。一旦人工把组件配成下拉，自动推断即让位 → 手工配置的下拉会
   覆盖掉原本可用的自动选项。这里给一键回填，避免「配了转换/颜色 + 手工下拉」后选项为空。 */
const importable = ref(false);
watch(
  () => props.record?.content_map,
  (cm) => {
    importable.value = !!cm && typeof cm === 'object' && !Array.isArray(cm) && Object.keys(cm).length > 0;
  },
  { immediate: true },
);

const importFromContentMap = () => {
  const cm = props.record?.content_map;
  if (!cm || typeof cm !== 'object') return;
  const vt = defaultValueType();
  const exist = new Set(rows.value.map((r: any) => String(coerceValue(r))));
  Object.keys(cm).forEach((k) => {
    const raw = vt === 'int' ? Number(k) : vt === 'bool' ? k === 'true' || k === '1' : k;
    const value = vt === 'int' && Number.isNaN(raw as number) ? k : raw;
    if (exist.has(String(value))) return;
    exist.add(String(value));
    rows.value.push({ valueType: vt, value, label: cm[k] ? String(cm[k]) : k });
  });
};
</script>

<style scoped lang="less">
.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
  margin: 8px 0;
  padding-top: 8px;
  border-top: 1px dashed rgba(0, 0, 0, 0.1);
}
.opt-tip {
  font-size: 12px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.45);
}
.opt-empty {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 8px;
}
.row-line {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}
.w-type {
  flex: none;
  width: 100px;
}
.w-val {
  flex: 1 1 0;
  min-width: 140px;
}
.w-label {
  flex: 1 1 0;
  min-width: 180px;
}
</style>
